import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faPlus, faPaperPlane, faRotateLeft,
  faTrashCan, faUser, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import CalendarPicker from '../util/CalendarPicker';
import TimePicker from '../util/TimePicker';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Select from 'react-select';
import { fxSelectStyles, buildCatOptions } from '../modal/AddExpenses';
import LoadingCenter from '../util/LoadingCenter';
import { entryMinutes, fmtDuration } from './tsHelpers';
import { getCompanyTimeStep } from '../util/timeStep';

dayjs.extend(isoWeek);

interface Props {
  access: any;
  taskOrTasks: boolean;
  refreshUserHeder?: () => void;
}

interface TaskOption { value: number; label: string; }

type Form = { open: boolean; dayIdx: number; editId: number | null };


// react-select стиль для мобильного дроплиста задач
const taskSelStyles: any = {
  control: (b: any) => ({ ...b, minHeight: 46, background: 'var(--bg-input)', borderColor: 'var(--border-input)', borderRadius: 10, boxShadow: 'none', fontSize: 15 }),
  valueContainer: (b: any) => ({ ...b, padding: '2px 10px' }),
  menuPortal: (b: any) => ({ ...b, zIndex: 9999 }),
  menu: (b: any) => ({ ...b, background: 'var(--bg-form)', zIndex: 9999 }),
  option: (b: any, s: any) => ({ ...b, background: s.isFocused ? 'var(--bg-top)' : 'transparent', color: 'var(--text-color)', fontSize: 15 }),
  multiValue: (b: any) => ({ ...b, background: 'rgba(37,99,235,.12)', borderRadius: 6 }),
  multiValueLabel: (b: any) => ({ ...b, color: '#2563eb', fontWeight: 700 }),
  multiValueRemove: (b: any) => ({ ...b, color: '#2563eb', ':hover': { background: 'rgba(37,99,235,.2)' } }),
  singleValue: (b: any) => ({ ...b, color: 'var(--text-color)' }),
  placeholder: (b: any) => ({ ...b, color: 'var(--text-color-calendar-week)' }),
  input: (b: any) => ({ ...b, color: 'var(--text-color)' }),
};

const MobileTimesheets: React.FC<Props> = ({ taskOrTasks, refreshUserHeder }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const fmtMin = (min: number) => fmtDuration(min, t);

  const [week, setWeek] = useState<Dayjs[]>(() => {
    const mon = dayjs().startOf('isoWeek' as any);
    return Array.from({ length: 7 }, (_, i) => mon.add(i, 'day'));
  });
  const [reports, setReports] = useState<any[]>([]);
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const [perWeek, setPerWeek] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [openSvId, setOpenSvId] = useState<number | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [form, setForm] = useState<Form>({ open: false, dayIdx: 0, editId: null });

  // Форма записи
  const [selCatId, setSelCatId] = useState<number | null>(null);
  const [selTasks, setSelTasks] = useState<TaskOption[]>([]);
  const [catTasks, setCatTasks] = useState<TaskOption[]>([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [desc, setDesc] = useState('');

  const weekStart = week[0];
  const weekEnd = week[6];
  const transformedWeek = useMemo(() => week.map((d) => d.format('DD.MM.YYYY')), [week]);

  const fetchReports = useCallback(async () => {
    try {
      const { data } = await axios.get('/time-reports');
      setReports(Array.isArray(data) ? data : []);
    } catch {
      // auth ошибки обрабатывает глобальный interceptor
    }
  }, []);

  const fetchMeta = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: user }, { data: cats }, { data: rep }] = await Promise.all([
        axios.get('user'),
        axios.get('category'),
        axios.get('/time-reports'),
      ]);
      if (user) { setPerWeek(user.pattern?.hoursPerWeek ?? 0); }
      setAllCategory((cats || []).filter((c: any) => c.status !== 'inactive'));
      setReports(Array.isArray(rep) ? rep : []);
    } catch {
      // interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // Опции категорий (как в Finance): плоский список с полным путём, алфавит
  const catOptions = useMemo(
    () => buildCatOptions(allCategory).sort((a: any, b: any) => a.label.localeCompare(b.label)),
    [allCategory],
  );

  // Задачи выбранной категории
  useEffect(() => {
    if (!selCatId) { setCatTasks([]); return; }
    let cancelled = false;
    axios.get(`category/${selCatId}`)
      .then(({ data }) => { if (!cancelled) setCatTasks((data.task || []).filter((tk: any) => tk.active).map((tk: any) => ({ value: tk.id, label: tk.name }))); })
      .catch(() => { if (!cancelled) setCatTasks([]); });
    return () => { cancelled = true; };
  }, [selCatId]);

  // Записи недели, сгруппированные по дню + итоги
  const { grouped, weekTotal, weekRows } = useMemo(() => {
    const rows = (reports || []).filter((r: any) => transformedWeek.includes(moment(r.date).format('DD.MM.YYYY')));
    const g: Record<string, any[]> = {};
    const totals: Record<string, number> = {};
    let total = 0;
    rows.forEach((r: any) => {
      const key = moment(r.date).format('YYYY-MM-DD');
      const min = entryMinutes(r);
      (g[key] = g[key] || []).push(r);
      totals[key] = (totals[key] || 0) + min;
      total += min;
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf()));
    return { grouped: g, dayTotals: totals, weekTotal: total, weekRows: rows };
  }, [reports, transformedWeek]);

  const dayStatus = (rows: any[]): string | null => {
    if (!rows.length) return null;
    const s = rows.map((r) => r.status);
    if (s.includes('pending')) return 'pending';
    if (s.includes('rejected')) return 'rejected';
    if (s.every((x) => x === 'fulfilled')) return 'fulfilled';
    return 'new';
  };
  const weekStatus = dayStatus(weekRows);
  const canEdit = weekStatus === null || weekStatus === 'new' || weekStatus === 'rejected';
  // Добавлять записи можно на любой неделе, кроме полностью одобренной.
  // (canEdit блокирует редактирование pending-недели, но добавить новую запись — можно)
  const canAdd = weekStatus !== 'fulfilled';
  const normMin = perWeek * 60;
  const progress = normMin > 0 ? Math.min(100, Math.round((weekTotal / normMin) * 100)) : 0;

  const STATUS_LABEL: Record<string, string> = {
    new: t('Draft') as string, pending: t('Pending') as string,
    fulfilled: t('Fulfilled') as string, rejected: t('Rejected') as string,
  };

  const shiftWeek = (delta: number) => { setWeek(week.map((d) => d.add(delta * 7, 'day'))); };
  const goToday = () => {
    const mon = dayjs().startOf('isoWeek' as any);
    setWeek(Array.from({ length: 7 }, (_, i) => mon.add(i, 'day')));
  };

  // Дефолтное время: 09:00–17:00; если в дне уже есть записи — старт = конец последней
  const defaultTimesFor = (dayIdx: number) => {
    const key = week[dayIdx]?.format('YYYY-MM-DD');
    const rows = (key && grouped[key]) || [];
    if (rows.length) return { start: moment(rows[rows.length - 1].endTime).format('HH:mm'), end: '17:00' };
    return { start: '09:00', end: '17:00' };
  };

  const resetForm = () => {
    setSelCatId(null); setSelTasks([]); setCatTasks([]);
    setStart(''); setEnd(''); setDesc('');
  };
  const openAddForm = (dayIdx: number) => {
    resetForm();
    const d = defaultTimesFor(dayIdx);
    setStart(d.start); setEnd(d.end);
    setForm({ open: true, dayIdx, editId: null });
  };
  const openEditForm = (row: any, dayIdx: number) => {
    setSelCatId(row.categoryId ?? null);
    setSelTasks((row.tasks || []).map((tk: any) => ({ value: tk.id, label: tk.name })));
    setStart(moment(row.startTime).format('HH:mm'));
    setEnd(moment(row.endTime).format('HH:mm'));
    setDesc(row.description || '');
    setForm({ open: true, dayIdx, editId: row.id });
  };
  const closeForm = () => { setForm((f) => ({ ...f, open: false })); };
  // Смена даты записи (в пределах недели)
  const changeFormDay = (dayIdx: number) => {
    if (!form.editId) { const d = defaultTimesFor(dayIdx); setStart(d.start); setEnd(d.end); }
    setForm((f) => ({ ...f, dayIdx }));
  };

  const openFabAdd = () => {
    const todayIdx = week.findIndex((d) => d.isSame(dayjs(), 'day'));
    const di = todayIdx >= 0 ? todayIdx : 0;
    openAddForm(di);
  };

  const saveEntry = async () => {
    const day = week[form.dayIdx];
    if (!selCatId) { enqueueSnackbar(t('SelectCategory') as string, { variant: 'error' }); return; }
    if (!selTasks.length) { enqueueSnackbar(t('SelectTask') as string, { variant: 'error' }); return; }
    if (!start || !end) { enqueueSnackbar(t('EnterTime') as string, { variant: 'error' }); return; }
    const isMidnight = end === '00:00';
    if (!isMidnight && start >= end) { enqueueSnackbar(t('EndAfterStart') as string, { variant: 'error' }); return; }

    const dayStr = day.format('YYYY-MM-DD');
    const endDayStr = isMidnight ? day.add(1, 'day').format('YYYY-MM-DD') : dayStr;
    const dateNoon = day.hour(12).minute(0).second(0).millisecond(0).toDate();
    const taskIds = selTasks.map((o) => o.value);

    setBusy(true);
    try {
      if (form.editId) {
        await axios.put(`time-reports/update-reports/${form.editId}`, {
          date: dateNoon, startTime: `${dayStr} ${start}:00`, endTime: `${endDayStr} ${end}:00`,
          categoryId: selCatId, taskId: taskIds, description: desc,
        });
        enqueueSnackbar(t('EntryUpdated') as string, { variant: 'success' });
      } else {
        await axios.post('/time-reports', {
          date: dateNoon, startTime: `${dayStr} ${start}:00`, endTime: `${endDayStr} ${end}:00`,
          categoryId: selCatId, description: desc, tasksId: taskIds,
        });
        enqueueSnackbar(t('EntryAdded') as string, { variant: 'success' });
      }
      await fetchReports();
      closeForm(); resetForm();
    } catch {
      enqueueSnackbar(t('SaveError') as string, { variant: 'error' });
    } finally { setBusy(false); }
  };

  const deleteEntry = async (id: number): Promise<boolean> => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(t('ConfirmDelete') as string)) return false;
    setBusy(true);
    try {
      await axios.post(`/time-reports/delete-reports/${id}`);
      await fetchReports();
      enqueueSnackbar(t('Deleted') as string, { variant: 'success' });
      return true;
    } catch {
      enqueueSnackbar(t('DeleteError') as string, { variant: 'error' });
      return false;
    } finally { setBusy(false); }
  };

  const changeStatus = async (ids: number[], status: string) => {
    if (!ids.length) return;
    await axios.put('/time-reports/update-status', { reportsId: ids, status });
  };
  const submitWeek = async () => {
    const ids = weekRows.filter((r) => r.status !== 'fulfilled' && r.status !== 'pending').map((r) => r.id);
    if (!ids.length) return;
    setBusy(true);
    try {
      await changeStatus(ids, 'pending'); await fetchReports(); refreshUserHeder?.();
      enqueueSnackbar(t('SentForApproval') as string, { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || (t('SaveError') as string), { variant: 'error' });
    } finally { setBusy(false); setConfirmSend(false); }
  };
  const returnWeek = async () => {
    const ids = weekRows.map((r) => r.id);
    setBusy(true);
    try {
      await changeStatus(ids, 'new'); await fetchReports(); refreshUserHeder?.();
      enqueueSnackbar(t('WeekReturned') as string, { variant: 'success' });
    } catch { enqueueSnackbar(t('SaveError') as string, { variant: 'error' }); } finally { setBusy(false); }
  };

  if (loading) return <LoadingCenter />;

  const formDay = week[form.dayIdx];
  // Плоский список всех записей недели, отсортированный по дате и времени начала
  const weekEntries = [...weekRows].sort((a, b) => {
    const da = moment(a.date), db = moment(b.date);
    if (!da.isSame(db, 'day')) return da.valueOf() - db.valueOf();
    return moment(a.startTime).valueOf() - moment(b.startTime).valueOf();
  });

  return (
    <div className="m-ts">
      {/* Header: выбор недели по центру, общее время под названием */}
      <div className="m-ts-hdr">
        <div className="m-ts-hdr-wk">
          <button type="button" className="m-ts-nav" onClick={() => shiftWeek(-1)} aria-label="prev"><FontAwesomeIcon icon={faChevronLeft} /></button>
          <button type="button" className="m-ts-hdr-lbl" onClick={goToday}>
            <b>{weekStart.format('D MMM')} – {weekEnd.format('D MMM')}</b>
            <small>{t('Week')} {weekStart.isoWeek()}</small>
            <span className="m-ts-hdr-lbl-total">{fmtMin(weekTotal)}{perWeek > 0 && <em> /{perWeek}{t('hShort')}</em>}</span>
          </button>
          <button type="button" className="m-ts-nav" onClick={() => shiftWeek(1)} aria-label="next"><FontAwesomeIcon icon={faChevronRight} /></button>
        </div>
      </div>
      {(perWeek > 0 || weekStatus) && (
        <div className="m-ts-progress-row">
          {perWeek > 0 && <div className="m-ts-bar"><i style={{ width: `${progress}%` }} /></div>}
          {weekStatus && <span className={`sbadge ${weekStatus}`}>{STATUS_LABEL[weekStatus]}</span>}
        </div>
      )}

      {/* Плоский список записей (Вариант 4) */}
      {weekEntries.length === 0 ? (
        <div className="m-ts-noentries" style={{ padding: '30px 0' }}>{t('NoEntriesWeek')}</div>
      ) : (
        <div className="m-ts-list">
          {weekEntries.map((r) => {
            const editable = r.status === 'new' || r.status === 'rejected';
            const d = moment(r.date);
            const dayIdx = week.findIndex((w) => w.isSame(dayjs(r.date), 'day'));
            const sv = r.supervisor ? `${r.supervisor.first_name ?? ''} ${r.supervisor.last_name ?? ''}`.trim() : '';
            const taskNames = (r.tasks || []).map((tk: any) => tk.name).join(', ');
            const Row: any = editable ? 'button' : 'div';
            return (
              <Row key={r.id} type={editable ? 'button' : undefined} className={`m-v4${editable ? ' tap' : ''}`} onClick={editable ? () => openEditForm(r, dayIdx >= 0 ? dayIdx : 0) : undefined}>
                <div className="m-v4-l">
                  <div className="m-v4-day">{d.format('dd, D MMM')}</div>
                  <div className="m-v4-task">{taskNames || r.categoryTree || '—'}</div>
                  <div className="m-v4-cat">
                    <span className="m-v4-dot" />
                    <span className="m-v4-catname">{r.categoryTree || '—'}</span>
                    {sv && (
                      <span className="m-v4-sv" onClick={(e) => { e.stopPropagation(); setOpenSvId(openSvId === r.id ? null : r.id); }}>
                        <FontAwesomeIcon icon={faUser} />
                        {openSvId === r.id && (
                          <span className="m-sv-pop"><span className="l">{t('Supervisor')}</span><span className="n">{sv}</span></span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="m-v4-r">
                  <div className="m-v4-hrs">{fmtMin(entryMinutes(r))}</div>
                  <div className="m-v4-time">{moment(r.startTime).format('HH:mm')}–{moment(r.endTime).format('HH:mm')}</div>
                  <span className={`sbadge ${r.status}`}>{STATUS_LABEL[r.status]}</span>
                </div>
              </Row>
            );
          })}
        </div>
      )}
      {openSvId !== null && <div className="m-sv-scrim" onClick={() => setOpenSvId(null)} />}

      {weekRows.length > 0 && canEdit && (
        <button type="button" className="btn-primary m-ts-submit" disabled={busy} onClick={() => setConfirmSend(true)}><FontAwesomeIcon icon={faPaperPlane} /> {t('SendForApproval')}</button>
      )}
      {weekStatus === 'pending' && (
        <button type="button" className="m-ts-return" disabled={busy} onClick={returnWeek}><FontAwesomeIcon icon={faRotateLeft} /> {t('ReturnWeek')}</button>
      )}

      {/* Предупреждение перед отправкой отчёта */}
      {confirmSend && (
        <div className="m-confirm-scrim" onClick={() => !busy && setConfirmSend(false)}>
          <div className="m-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="m-confirm-ic"><FontAwesomeIcon icon={faTriangleExclamation} /></div>
            <div className="m-confirm-title">{t('SendWeekTitle')}</div>
            <div className="m-confirm-msg">{t('SendWeekWarning')}</div>
            <div className="m-confirm-actions">
              <button type="button" className="m-ts-cancel" disabled={busy} onClick={() => setConfirmSend(false)}>{t('Cancel')}</button>
              <button type="button" className="btn-primary m-confirm-ok" disabled={busy} onClick={submitWeek}><FontAwesomeIcon icon={faPaperPlane} /> {t('Send')}</button>
            </div>
          </div>
        </div>
      )}

      {canAdd && !form.open && (
        <button type="button" className="m-ts-fab" onClick={openFabAdd} aria-label={t('AddEntry') as string}><FontAwesomeIcon icon={faPlus} /></button>
      )}

      {/* Полноэкранная форма записи */}
      {form.open && (
        <div className="m-fs">
          <div className="m-fs-head">
            <button type="button" className="m-fs-back" onClick={closeForm} aria-label="back"><FontAwesomeIcon icon={faChevronLeft} /></button>
            <div className="m-fs-title">{form.editId ? t('EditEntry') : t('NewEntry')}</div>
            {form.editId ? (
              <button type="button" className="m-fs-del" disabled={busy} onClick={async () => { if (form.editId && await deleteEntry(form.editId)) closeForm(); }} aria-label="delete"><FontAwesomeIcon icon={faTrashCan} /></button>
            ) : <div style={{ width: 36, flexShrink: 0 }} />}
          </div>

          <>
              <div className="m-fs-body">
                {/* Дата записи (в пределах недели) */}
                <div className="m-ts-field">
                  <label>{t('Date')}</label>
                  <CalendarPicker
                    mode="single"
                    variant="bare"
                    className="m-ts-input m-ts-timebtn"
                    value={formDay?.toDate()}
                    minDate={week[0].toDate()}
                    maxDate={week[6].toDate()}
                    onChange={(d: Date | null) => { if (d) { const i = week.findIndex((w) => w.isSame(dayjs(d), 'day')); if (i >= 0) changeFormDay(i); } }}
                    displayFormat="dddd, D MMM"
                  />
                </div>

                {/* Категория → react-select (как в Finance) */}
                <div className="m-ts-field">
                  <label>{t('CategoryTask')}</label>
                  <Select
                    options={catOptions}
                    value={selCatId ? (catOptions.find((o: any) => o.value === selCatId) || null) : null}
                    onChange={(o: any) => { setSelCatId(o ? o.value : null); setSelTasks([]); }}
                    placeholder={t('SelectCategory') as string}
                    styles={fxSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    isClearable
                    noOptionsMessage={() => '—'}
                  />
                </div>
                {selCatId && (
                  <div className="m-ts-field">
                    <label>{t('SelectTask')}</label>
                    <Select
                      isMulti={taskOrTasks}
                      options={catTasks}
                      value={(taskOrTasks ? selTasks : (selTasks[0] || null)) as any}
                      onChange={(v: any) => setSelTasks(taskOrTasks ? (v || []) : (v ? [v] : []))}
                      placeholder={t('SelectTask') as string}
                      styles={taskSelStyles}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      noOptionsMessage={() => '—'}
                    />
                  </div>
                )}

                {/* Время с шагом компании */}
                <div className="m-ts-two">
                  <div className="m-ts-field">
                    <label>{t('Start')}</label>
                    <TimePicker
                      mode="single"
                      variant="bare"
                      output="string"
                      className="m-ts-input m-ts-timebtn"
                      value={start}
                      onChange={(v: any) => setStart(v)}
                      step={getCompanyTimeStep()}
                    />
                  </div>
                  <div className="m-ts-field">
                    <label>{t('End')}</label>
                    <TimePicker
                      mode="single"
                      variant="bare"
                      output="string"
                      className="m-ts-input m-ts-timebtn"
                      value={end}
                      onChange={(v: any) => setEnd(v)}
                      step={getCompanyTimeStep()}
                    />
                  </div>
                </div>

                <div className="m-ts-field">
                  <label>{t('Description')}</label>
                  <textarea className="m-ts-input" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t('WhatDidYouDo') as string} />
                </div>
              </div>

              <div className="m-fs-foot">
                <button type="button" className="m-ts-cancel" onClick={closeForm}>{t('Cancel')}</button>
                <button type="button" className="btn-primary" disabled={busy} onClick={saveEntry}>{t('Save')}</button>
              </div>
            </>
        </div>
      )}
    </div>
  );
};

export default MobileTimesheets;
